import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Paper,
    Button,
    Divider,
    Card,
    CardContent,
    Slider,
    useTheme,
    useMediaQuery,
    alpha
} from "@mui/material";
import { styled } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

// MUI ikoonid
import StoreIcon from "@mui/icons-material/Store";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import DescriptionIcon from "@mui/icons-material/Description";
import SEO from "../components/SEO";

// Värvipalett
const colors = {
    primary: "#0072E5",
    secondary: "#333333",
    accent: "#FF5722",
    success: "#4caf50",
    error: "#f44336",
    lightGray: "#F8F9FA",
    darkGray: "#212529",
    white: "#FFFFFF",
    statsBg: "rgba(0, 114, 229, 0.05)"
};

const montonioSupportedPayments = [
    {
        country: "Estonia 🇪🇪",
        methods: "Swedbank, SEB, LHV, Luminor, Coop Pank, Citadele, N26, Revolut"
    },
    {
        country: "Latvia 🇱🇻",
        methods: "Swedbank, SEB, Luminor, Citadele"
    },
    {
        country: "Lithuania 🇱🇹",
        methods: "Swedbank, SEB, Luminor, Citadele, Artea, N26, Revolut"
    },
    {
        country: "Finland 🇫🇮",
        methods: "Nordea, Danske Bank, N26, Handelsbanken, Oma Säästöpankki, POP Pankki, Säästopankki, Ålandsbanken, OP, S-Pankki"
    },
    {
        country: "Poland 🇵🇱",
        methods: "Alior Bank, Bank Pekao, Bank Pocztowy, Bank Polski, BNP Paribas, Crédit Agricole, ING, Inteligo Bank, mBank, Millenium Bank, N26, Erste, Revolut"
    }
];

// Ümmargune ikooni-taust
const IconCircle = styled(Box)(({ theme }) => ({
    width: 64,
    height: 64,
    borderRadius: "50%",
    backgroundColor: alpha(colors.primary, 0.1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(2),
    transition: "all 0.3s ease",
}));

// Funktsioon linnukese või X-i näitamiseks
const FeatureItem = ({ available, text, delay = 0 }) => {
    return (
        <Box
            component={motion.div}
            initial={{ x: -10, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ delay: delay * 0.05 + 0.3, duration: 0.4 }}
            viewport={{once: true}}
            sx={{
                display: "flex",
                alignItems: "flex-start",
                mb: 1.8,
                transition: "transform 0.2s ease",
                "&:hover": {
                    transform: "translateX(5px)"
                }
            }}
        >
            {available ? (
                <CheckCircleOutlineIcon sx={{ color: colors.success, mr: 1.5, mt: 0.2, flexShrink: 0 }} />
            ) : (
                <CancelOutlinedIcon sx={{ color: colors.error, mr: 1.5, mt: 0.2, flexShrink: 0 }} />
            )}
            <Typography
                variant="body1"
                sx={{
                    lineHeight: 1.4,
                    color: available ? "text.primary" : "text.secondary"
                }}
            >
                {text}
            </Typography>
        </Box>
    );
};

// PricingCard komponent
const PricingCard = ({
                         icon,
                         title,
                         price,
                         features,
                         recommended = false,
                         delay = 0.3
                     }) => {
    return (
        <Card
            component={motion.div}
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay, duration: 0.5 }}
            viewport={{once: true}}
            elevation={recommended ? 4 : 2}
            sx={{
                borderRadius: 4,
                overflow: 'hidden',
                height: '100%',
                position: 'relative',
                border: recommended ? `2px solid ${colors.primary}` : 'none',
                transition: "all 0.3s ease",
                "&:hover": {
                    transform: "translateY(-10px)",
                    boxShadow: recommended ? '0 15px 35px rgba(0,114,229,0.3)' : '0 12px 30px rgba(0,0,0,0.1)',
                }
            }}
        >
            {recommended && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 20,
                        right: -35,
                        backgroundColor: colors.primary,
                        color: "white",
                        py: 0.6,
                        px: 4,
                        transform: "rotate(45deg)",
                        transformOrigin: "top right",
                        boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                        zIndex: 1
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: "700" }}>
                        TOP
                    </Typography>
                </Box>
            )}

            <CardContent sx={{ p: 4, pt: recommended ? 5 : 4 }}>
                <Box sx={{ textAlign: "center", mb: 3 }}>
                    <IconCircle
                        sx={{
                            mx: "auto",
                            backgroundColor: recommended ? alpha(colors.primary, 0.15) : alpha(colors.primary, 0.1),
                        }}
                    >
                        {React.cloneElement(icon, {
                            sx: {
                                fontSize: 32,
                                color: colors.primary
                            }
                        })}
                    </IconCircle>

                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: "bold",
                            mb: 1.5,
                            fontFamily: '"Montserrat", sans-serif',
                        }}
                    >
                        {title}
                    </Typography>

                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: "bold",
                            color: colors.primary,
                            display: "flex",
                            alignItems: "baseline",
                            justifyContent: "center",
                            mb: 0.5,
                            fontFamily: '"Montserrat", sans-serif',
                        }}
                    >
                        {price}
                        <Typography
                            component="span"
                            variant="body1"
                            sx={{
                                ml: 1,
                                color: "text.secondary",
                                fontWeight: "normal"
                            }}
                        >
                            /month
                        </Typography>
                    </Typography>
                </Box>

                <Divider sx={{ my: 2.5 }} />

                <Box sx={{ mb: 3 }}>
                    {features.map((feature, index) => (
                        <FeatureItem
                            key={index}
                            available={feature.available}
                            text={feature.text}
                            delay={index}
                        />
                    ))}
                </Box>

                <Button
                    variant={recommended ? "contained" : "outlined"}
                    color="primary"
                    size="large"
                    fullWidth
                    sx={{
                        mt: 2,
                        py: 1.2,
                        borderRadius: 2,
                        textTransform: "none",
                        fontSize: "1rem",
                        fontWeight: 600,
                        boxShadow: recommended ? "0 4px 14px rgba(0,114,229,0.4)" : "none",
                        transition: "all 0.3s",
                        "&:hover": {
                            transform: "translateY(-3px)",
                            boxShadow: recommended ? "0 6px 20px rgba(0,114,229,0.6)" : "0 4px 12px rgba(0,0,0,0.1)",
                        }
                    }}
                    onClick={() => window.location.href = "/register"}
                >
                    Get Started
                </Button>
            </CardContent>
        </Card>
    );
};

// InfoCard komponent
const InfoCard = ({ icon, title, description, buttonText, buttonLink, delay = 0.3 }) => {
    return (
        <Paper
            component={motion.div}
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay, duration: 0.5 }}
            viewport={{once: true}}
            elevation={1}
            sx={{
                p: 4,
                borderRadius: 4,
                height: "100%",
                transition: "all 0.3s ease",
                "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
                }
            }}
        >
            <IconCircle>
                {React.cloneElement(icon, {
                    sx: {
                        fontSize: 32,
                        color: colors.primary
                    }
                })}
            </IconCircle>
            <Typography
                variant="h6"
                sx={{
                    fontWeight: "bold",
                    mb: 2,
                    fontFamily: '"Montserrat", sans-serif',
                }}
            >
                {title}
            </Typography>
            <Box
                dangerouslySetInnerHTML={{ __html: description }}
                sx={{
                    mb: 2,
                    color: 'text.secondary',
                    lineHeight: 1.6,
                    typography: 'body2',
                    '& strong': {
                        fontWeight: 700,
                    }
                }}
            />
            {buttonText && (
                <Button
                    variant="outlined"
                    color="primary"
                    sx={{
                        mt: 1,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        transition: "all 0.3s ease",
                        "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: '0 4px 12px rgba(0,114,229,0.15)',
                        }
                    }}
                    onClick={() => window.location.href = buttonLink}
                >
                    {buttonText}
                </Button>
            )}
        </Paper>
    );
};

// ComparisonCard komponent
const ComparisonCard = ({ title, content, isHighlighted = false, delay = 0.3 }) => {
    return (
        <Paper
            component={motion.div}
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay, duration: 0.5 }}
            viewport={{once: true}}
            elevation={2}
            sx={{
                p: 4,
                borderRadius: 4,
                textAlign: "center",
                backgroundColor: isHighlighted ? alpha(colors.success, 0.1) : colors.white,
                border: isHighlighted ? `1px solid ${alpha(colors.success, 0.3)}` : 'none',
                transition: "all 0.3s ease",
                "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: isHighlighted ? '0 8px 25px rgba(76,175,80,0.15)' : '0 8px 25px rgba(0,0,0,0.08)',
                }
            }}
        >
            <Typography
                variant="h6"
                sx={{
                    fontWeight: "bold",
                    mb: 2,
                    color: isHighlighted ? colors.success : "inherit",
                    fontFamily: '"Montserrat", sans-serif',
                }}
            >
                {title}
            </Typography>
            <Box
                dangerouslySetInnerHTML={{ __html: content }}
                sx={{
                    '& strong': {
                        fontWeight: 700,
                    }
                }}
            />
        </Paper>
    );
};

// SectionTitle komponent
const SectionTitle = ({ title, subtitle, align = "center", delay = 0.2 }) => {
    return (
        <Box
            component={motion.div}
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay, duration: 0.5 }}
            viewport={{once: true}}
            sx={{
                mb: 5,
                textAlign: align,
                position: 'relative'
            }}
        >
            <Typography
                variant="h4"
                sx={{
                    fontWeight: "bold",
                    mb: 1.5,
                    fontFamily: '"Montserrat", sans-serif',
                    position: 'relative',
                    display: align === "center" ? 'inline-block' : 'block'
                }}
            >
                {title}
                <Box
                    sx={{
                        position: 'absolute',
                        height: 4,
                        width: 60,
                        backgroundColor: colors.accent,
                        bottom: -10,
                        left: align === "center" ? '50%' : 0,
                        transform: align === "center" ? 'translateX(-50%)' : 'none',
                        borderRadius: 2
                    }}
                />
            </Typography>
            {subtitle && (
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                        maxWidth: align === "center" ? 700 : 'unset',
                        mx: align === "center" ? "auto" : 0,
                        mt: 3,
                        lineHeight: 1.6
                    }}
                >
                    {subtitle}
                </Typography>
            )}
        </Box>
    );
};

// LargeBanner komponent
const LargeBanner = ({ text, color = colors.primary, delay = 0.3 }) => {
    return (
        <Box
            component={motion.div}
            initial={{ scale: 0.95, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            transition={{ delay, duration: 0.5 }}
            viewport={{once: true}}
            sx={{
                backgroundColor: alpha(color, 0.05),
                borderRadius: 4,
                p: 2,
                my: 5,
                textAlign: "center",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "6px",
                    backgroundColor: color,
                },
            }}
        >
            <Typography
                variant="h4"
                sx={{
                    fontWeight: "700",
                    color: color,
                    fontFamily: '"Montserrat", sans-serif',
                    my: 1,
                }}
            >
                {text}
            </Typography>
        </Box>
    );
};

export default function Pricing() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [monthlyPayments, setMonthlyPayments] = useState(100);

    const averagePaymentAmount = 80;
    const stripePercentFee = 0.014;
    const stripeFixedFee = 0.25;
    const montonioFixedFee = 0.1;
    const montonioPaymentLinkMonthlyFee = 10;
    const montonioMonthlyFee = 0;

    const totalVolume = monthlyPayments * averagePaymentAmount;
    const stripeCost = (totalVolume * stripePercentFee) + (monthlyPayments * stripeFixedFee);
    const montonioCost = (monthlyPayments * montonioFixedFee) + montonioPaymentLinkMonthlyFee + montonioMonthlyFee;
    const savings = stripeCost - montonioCost;

    const formatCurrency = (value) =>
        value.toLocaleString("en-EE", {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });

    // Pakettide funktsioonid
    const standardFeatures = [
        { available: true, text: "Add Classes & WODs" },
        { available: true, text: "Add and manage plans" },
        { available: true, text: "Add credit to users" },
        { available: true, text: "Member Tracking" },
        { available: true, text: "Add training plans to users" },
        { available: false, text: "Payment Management" },
        { available: false, text: "Email service" },
        { available: false, text: "Contract Management" },
    ];

    const premiumFeatures = [
        { available: true, text: "Add Classes & WODs" },
        { available: true, text: "Add and manage plans" },
        { available: true, text: "Add credit to users" },
        { available: true, text: "Priority Support" },
        { available: true, text: "Payment Management" },
        { available: true, text: "Automated payment links for monthly memberships" },
        { available: true, text: "Member Tracking" },
        { available: true, text: "Add training plans to users" },
        { available: true, text: "Email service" },
        { available: true, text: "Contract Management" },
        { available: true, text: "Affiliate Competition Management" },
        { available: true, text: "Online Shop & Product Sales" },
        { available: true, text: "Event Management & Ticket Sales" },
    ];

    return (
        <>
            <SEO
                title="Irontrack - Pricing Plans"
                description="Flexible pricing plans for CrossFit gyms and athletes."
                keywords="crossfit pricing, gym management pricing, fitness platform cost"
                canonicalUrl="https://www.irontrack.ee/pricing"
            />
            <Box sx={{ overflowX: "hidden" }}>
                {/** HERO SECTION **/}
                <Box sx={{
                    minHeight: {xs: "70vh", md: "80vh"},
                    background: 'white',
                    display: "flex",
                    alignItems: "center",
                    position: "relative",
                    overflow: "hidden",
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: `radial-gradient(circle at 20% 50%, ${alpha(colors.primary, 0.05)} 0%, transparent 50%),
                                    radial-gradient(circle at 80% 80%, ${alpha(colors.accent, 0.03)} 0%, transparent 50%)`,
                        zIndex: 0
                    }
                }}>
                    <Container maxWidth="lg" sx={{position: 'relative', zIndex: 2}}>
                        <Grid container spacing={4} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <Box
                                    component={motion.div}
                                    initial={{x: -50, opacity: 0}}
                                    animate={{x: 0, opacity: 1}}
                                    transition={{duration: 0.8}}
                                >
                                    <Typography
                                        variant={isMobile ? "h3" : "h2"}
                                        sx={{
                                            fontWeight: 800,
                                            mb: 3,
                                            mt: 1,
                                            color: colors.secondary,
                                            fontFamily: '"Montserrat", sans-serif',
                                            lineHeight: 1.2
                                        }}
                                    >
                                        Simple, Transparent Pricing
                                    </Typography>
                                    <Typography
                                        variant={isMobile ? "body1" : "h6"}
                                        sx={{
                                            mb: 4,
                                            color: 'text.secondary',
                                            lineHeight: 1.7,
                                            fontWeight: 400
                                        }}
                                    >
                                        Built for CrossFit affiliate owners and free for regular users.
                                        Leverage Montonio's cost-effective payment links to keep more of your revenue.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={() => navigate("/register")}
                                        sx={{
                                            py: 1.5,
                                            px: 4,
                                            fontSize: "1rem",
                                            fontWeight: 600,
                                            borderRadius: 2,
                                            textTransform: "none",
                                            bgcolor: colors.primary,
                                            color: 'white',
                                            boxShadow: "0 4px 14px rgba(0,114,229,0.3)",
                                            "&:hover": {
                                                bgcolor: '#0052a3',
                                                transform: "translateY(-2px)",
                                                boxShadow: "0 6px 20px rgba(0,114,229,0.4)",
                                            }
                                        }}
                                    >
                                        Get Started Today
                                    </Button>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box
                                    component={motion.div}
                                    initial={{x: 50, opacity: 0}}
                                    animate={{x: 0, opacity: 1}}
                                    transition={{duration: 0.8, delay: 0.2}}
                                    sx={{
                                        borderRadius: 4,
                                        overflow: 'hidden',
                                        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                                        position: 'relative',
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            background: `linear-gradient(135deg, ${alpha(colors.primary, 0.1)} 0%, transparent 100%)`,
                                            pointerEvents: 'none'
                                        }
                                    }}
                                >
                                    <img
                                        src="/pic/blog-1.jpg"
                                        alt="CrossFit Training"
                                        style={{width: '100%', height: 'auto', display: 'block'}}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Container>
                </Box>

                <Container sx={{ py: { xs: 6, md: 10 } }}>
                    {/* Account setup highlights */}
                    <Grid container spacing={6} alignItems="stretch" sx={{ mb: 8 }}>
                        <Grid item xs={12} md={6}>
                            <Box
                                component={motion.div}
                                initial={{ x: -40, opacity: 0 }}
                                whileInView={{ x: 0, opacity: 1 }}
                                transition={{ duration: 0.55 }}
                                viewport={{ once: true }}
                                sx={{
                                    p: { xs: 3, md: 4 },
                                    borderRadius: 4,
                                    height: "100%",
                                    backgroundColor: alpha(colors.primary, 0.05),
                                    border: `1px solid ${alpha(colors.primary, 0.18)}`
                                }}
                            >
                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontWeight: 700,
                                        mb: 3,
                                        fontFamily: '"Montserrat", sans-serif',
                                        position: 'relative',
                                        display: 'inline-block'
                                    }}
                                >
                                    Club Account Setup
                                    <Box sx={{
                                        position: 'absolute',
                                        height: 4,
                                        width: 60,
                                        backgroundColor: colors.accent,
                                        bottom: -10,
                                        left: 0,
                                        borderRadius: 2
                                    }}/>
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{ lineHeight: 1.8, fontSize: '1.05rem', mb: 3 }}
                                >
                                    To create a club account, register first.
                                    After sign up, send us a message at info@irontrack.ee and we will activate and configure your club account with you.
                                </Typography>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={() => navigate("/register")}
                                    sx={{
                                        py: 1.4,
                                        px: 4,
                                        borderRadius: 2,
                                        textTransform: "none",
                                        fontSize: "1rem",
                                        fontWeight: 600,
                                        boxShadow: "0 4px 14px rgba(0,114,229,0.3)",
                                        "&:hover": {
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 8px 20px rgba(0,114,229,0.4)",
                                        }
                                    }}
                                >
                                    Create Club Account
                                </Button>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box
                                component={motion.div}
                                initial={{ x: 40, opacity: 0 }}
                                whileInView={{ x: 0, opacity: 1 }}
                                transition={{ duration: 0.55, delay: 0.1 }}
                                viewport={{ once: true }}
                                sx={{
                                    p: { xs: 3, md: 4 },
                                    borderRadius: 4,
                                    height: "100%",
                                    backgroundColor: alpha(colors.success, 0.08),
                                    border: `1px solid ${alpha(colors.success, 0.26)}`
                                }}
                            >
                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontWeight: 700,
                                        mb: 3,
                                        fontFamily: '"Montserrat", sans-serif',
                                        position: 'relative',
                                        display: 'inline-block'
                                    }}
                                >
                                    Free for Regular Users
                                    <Box sx={{
                                        position: 'absolute',
                                        height: 4,
                                        width: 60,
                                        backgroundColor: colors.accent,
                                        bottom: -10,
                                        left: 0,
                                        borderRadius: 2
                                    }}/>
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{ lineHeight: 1.8, fontSize: '1.05rem', mb: 3 }}
                                >
                                    If you are a regular user, Irontrack is free to use.
                                    Track your trainings, follow your progress, and use your daily tools without subscription fees.
                                </Typography>
                                <Button
                                    variant="outlined"
                                    size="large"
                                    onClick={() => navigate("/register")}
                                    sx={{
                                        py: 1.4,
                                        px: 4,
                                        borderRadius: 2,
                                        textTransform: "none",
                                        fontSize: "1rem",
                                        fontWeight: 600,
                                        borderColor: alpha(colors.success, 0.5),
                                        color: colors.success,
                                        "&:hover": {
                                            borderColor: colors.success,
                                            backgroundColor: alpha(colors.success, 0.08),
                                            transform: "translateY(-2px)",
                                        }
                                    }}
                                >
                                    Sign Up Free
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>

                    {/* HOW IT WORKS SECTION WITH IMAGE */}
                    <Grid container spacing={6} alignItems="center" sx={{ mb: 8 }}>
                        <Grid item xs={12} md={6} sx={{display: {xs: 'none', md: 'block'}}}>
                            <Box
                                component={motion.div}
                                initial={{x: -50, opacity: 0}}
                                whileInView={{x: 0, opacity: 1}}
                                transition={{duration: 0.6}}
                                viewport={{once: true}}
                                sx={{borderRadius: 4, overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.1)'}}
                            >
                                <img src="/pic/blog-2.jpg" alt="How It Works" style={{width: '100%', height: 'auto', display: 'block'}} />
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box
                                component={motion.div}
                                initial={{x: 50, opacity: 0}}
                                whileInView={{x: 0, opacity: 1}}
                                transition={{duration: 0.6}}
                                viewport={{once: true}}
                            >
                                <Typography
                                    variant="h3"
                                    sx={{
                                        fontWeight: 700,
                                        mb: 3,
                                        fontFamily: '"Montserrat", sans-serif',
                                        position: 'relative',
                                        display: 'inline-block'
                                    }}
                                >
                                    How It Works
                                    <Box sx={{
                                        position: 'absolute',
                                        height: 4,
                                        width: 60,
                                        backgroundColor: colors.accent,
                                        bottom: -10,
                                        left: 0,
                                        borderRadius: 2
                                    }}/>
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{lineHeight: 1.8, fontSize: '1.1rem'}}>
                                    Sign up and create your account based on your use case.
                                    If you want a club account, create an account and let us know at info@irontrack.ee so we can help you set everything up.
                                    Regular users can use Irontrack for free.
                                    For payment features, create a Montonio account and we will guide you through the setup.
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>

                    {/* CrossFit CAP integration block (mirrored How It Works style) */}
                    <Grid container spacing={6} alignItems="center" sx={{ mb: 8 }}>
                        <Grid item xs={12} md={6}>
                            <Box
                                component={motion.div}
                                initial={{x: -50, opacity: 0}}
                                whileInView={{x: 0, opacity: 1}}
                                transition={{duration: 0.6}}
                                viewport={{once: true}}
                            >
                                <Typography
                                    variant="h3"
                                    sx={{
                                        fontWeight: 700,
                                        mb: 3,
                                        fontFamily: '"Montserrat", sans-serif',
                                        position: 'relative',
                                        display: 'inline-block'
                                    }}
                                >
                                    CrossFit CAP Integration
                                    <Box sx={{
                                        position: 'absolute',
                                        height: 4,
                                        width: 60,
                                        backgroundColor: colors.accent,
                                        bottom: -10,
                                        left: 0,
                                        borderRadius: 2
                                    }}/>
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{lineHeight: 1.8, fontSize: '1.1rem', mb: 2}}
                                >
                                    Irontrack supports CrossFit Affiliate CAP Programming integration.
                                    To activate this feature, please sign up first.
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{lineHeight: 1.8, fontSize: '1.1rem', mb: 3}}
                                >
                                    Access is available only to clubs that hold a valid CrossFit affiliate license and are in good standing.
                                </Typography>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={() => navigate("/register")}
                                    sx={{
                                        py: 1.5,
                                        px: 4,
                                        fontSize: "1rem",
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        textTransform: "none",
                                        bgcolor: colors.primary,
                                        color: 'white',
                                        boxShadow: "0 4px 14px rgba(0,114,229,0.3)",
                                        "&:hover": {
                                            bgcolor: '#0052a3',
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 6px 20px rgba(0,114,229,0.4)",
                                        }
                                    }}
                                >
                                    Sign Up
                                </Button>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6} sx={{display: {xs: 'none', md: 'block'}}}>
                            <Box
                                component={motion.div}
                                initial={{x: 50, opacity: 0}}
                                whileInView={{x: 0, opacity: 1}}
                                transition={{duration: 0.6}}
                                viewport={{once: true}}
                                sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                            >
                                <img
                                    src="/pic/CrossFit-Logo.png"
                                    alt="CrossFit CAP Programming"
                                    style={{width: '100%', maxWidth: '420px', height: 'auto', display: 'block'}}
                                />
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Stebby integration block (mirrored vs CAP) */}
                    <Grid container spacing={6} alignItems="center" sx={{ mb: 8 }}>
                        <Grid item xs={12} md={6} sx={{display: {xs: 'none', md: 'block'}}}>
                            <Box
                                component={motion.div}
                                initial={{x: -50, opacity: 0}}
                                whileInView={{x: 0, opacity: 1}}
                                transition={{duration: 0.6}}
                                viewport={{once: true}}
                                sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                            >
                                <img
                                    src="/pic/stebby-logo-gradient.png"
                                    alt="Stebby Integration"
                                    style={{width: '100%', maxWidth: '320px', height: 'auto', display: 'block'}}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box
                                component={motion.div}
                                initial={{x: 50, opacity: 0}}
                                whileInView={{x: 0, opacity: 1}}
                                transition={{duration: 0.6}}
                                viewport={{once: true}}
                            >
                                <Typography
                                    variant="h3"
                                    sx={{
                                        fontWeight: 700,
                                        mb: 3,
                                        fontFamily: '"Montserrat", sans-serif',
                                        position: 'relative',
                                        display: 'inline-block'
                                    }}
                                >
                                    Stebby Integration
                                    <Box sx={{
                                        position: 'absolute',
                                        height: 4,
                                        width: 60,
                                        backgroundColor: colors.accent,
                                        bottom: -10,
                                        left: 0,
                                        borderRadius: 2
                                    }}/>
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{lineHeight: 1.8, fontSize: '1.1rem', mb: 2}}
                                >
                                    We support Stebby integration for affiliates using Stebby benefits.
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{lineHeight: 1.8, fontSize: '1.1rem'}}
                                >
                                    Users can purchase a ticket in Stebby and activate it immediately in Irontrack,
                                    so they can register to classes without waiting.
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>

                    <SectionTitle
                        title="Simple Plans for Every Need"
                        subtitle="Whether you're an affiliate owner looking for a powerful gym management solution or an athlete wanting to track your progress—our pricing is clear, transparent, and helps you focus on what matters most: building a thriving community."
                    />

                    <LargeBanner text="Packages for affiliate owner" color={colors.primary} delay={0.4} />

                    <Grid container spacing={4} sx={{ mt: 2 }}>
                        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                            <PricingCard
                                icon={<DescriptionIcon />}
                                title="Standard"
                                price="€60"
                                features={standardFeatures}
                                delay={0.3}
                            />
                        </Grid>

                        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                            <PricingCard
                                icon={<StoreIcon />}
                                title="Premium"
                                price="€120"
                                features={premiumFeatures}
                                recommended={true}
                                delay={0.5}
                            />
                        </Grid>
                    </Grid>
                </Container>

                {/* IMAGE SECTION - COMMUNITY */}
                <Box sx={{bgcolor: colors.lightGray, py: {xs: 6, md: 10}}}>
                    <Container>
                        <Grid container spacing={6} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <Box
                                    component={motion.div}
                                    initial={{x: -50, opacity: 0}}
                                    whileInView={{x: 0, opacity: 1}}
                                    transition={{duration: 0.6}}
                                    viewport={{once: true}}
                                >
                                    <Typography variant="h3" sx={{fontWeight: 700, mb: 3, fontFamily: '"Montserrat", sans-serif'}}>
                                        Save More, Invest in Your Community
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary" sx={{lineHeight: 1.8, mb: 3, fontSize: '1.1rem'}}>
                                        With lower transaction fees through Montonio integration, you can keep more of your revenue
                                        and reinvest it where it matters most—your members and your facility.
                                    </Typography>
                                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                                        <Box sx={{display: 'flex', alignItems: 'start', gap: 2}}>
                                            <Box sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                bgcolor: alpha(colors.primary, 0.1),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}>
                                                <CheckCircleOutlineIcon sx={{color: colors.success, fontSize: 20}} />
                                            </Box>
                                            <Typography variant="body1" color="text.secondary">
                                                Lower payment processing fees compared to traditional platforms
                                            </Typography>
                                        </Box>
                                        <Box sx={{display: 'flex', alignItems: 'start', gap: 2}}>
                                            <Box sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                bgcolor: alpha(colors.primary, 0.1),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}>
                                                <CheckCircleOutlineIcon sx={{color: colors.success, fontSize: 20}} />
                                            </Box>
                                            <Typography variant="body1" color="text.secondary">
                                                Transparent pricing with no hidden costs
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box
                                    component={motion.div}
                                    initial={{x: 50, opacity: 0}}
                                    whileInView={{x: 0, opacity: 1}}
                                    transition={{duration: 0.6}}
                                    viewport={{once: true}}
                                    sx={{borderRadius: 4, overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.1)'}}
                                >
                                    <img src="/pic/blog-3.jpg" alt="Community Investment" style={{width: '100%', height: 'auto', display: 'block'}} />
                                </Box>
                            </Grid>
                        </Grid>
                    </Container>
                </Box>

                <Container sx={{ py: { xs: 6, md: 10 } }}>
                    {/* Montonio Integration Information */}
                    <Box
                        component={motion.div}
                        initial={{ y: 20, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.6, duration: 0.5 }}
                        viewport={{once: true}}
                        sx={{
                            mt: 8,
                            mb: 4,
                            p: 4,
                            backgroundColor: alpha(colors.primary, 0.05),
                            borderRadius: 4,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                        }}
                    >
                        <Grid container spacing={4} alignItems="center">
                            <Grid item xs={12} md={2} sx={{ textAlign: "center" }}>
                                <IconCircle sx={{ mx: "auto", width: 70, height: 70 }}>
                                    <CreditCardIcon sx={{ fontSize: 36, color: colors.primary }} />
                                </IconCircle>
                            </Grid>
                            <Grid item xs={12} md={7}>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: "bold",
                                        mb: 2,
                                        fontFamily: '"Montserrat", sans-serif',
                                    }}
                                >
                                    Montonio Integration (Optional)
                                </Typography>

                                <Typography
                                    variant="body1"
                                    sx={{ lineHeight: 1.6 }}
                                >
                                    Use Montonio payment links for lower bank payment costs compared to typical card processors. Seamlessly integrate
                                    with your subscription plan for enhanced payment management. <strong>Montonio fees are not included in Irontrack package prices. Current bank payment pricing: 0€ monthly fee, 10€ payment link monthly fee, and 0.10€ per bank payment. NB! Card payments may have different pricing.</strong>
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={3} sx={{ textAlign: "center" }}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="large"
                                    onClick={() => (window.location.href = "https://montonio.com/et/hinnapaketid/")}
                                    sx={{
                                        width: "100%",
                                        py: 1.2,
                                        borderRadius: 2,
                                        textTransform: "none",
                                        fontSize: "1rem",
                                        fontWeight: 600,
                                        transition: "all 0.3s",
                                        "&:hover": {
                                            transform: "translateY(-3px)",
                                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                        }
                                    }}
                                >
                                    Explore Montonio
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Container>

                {/** MONTONIO PAYMENT PACKAGES **/}
                <Box sx={{ backgroundColor: colors.lightGray, py: { xs: 6, md: 10 } }}>
                    <Container>
                        <SectionTitle
                            title="Montonio Pricing Overview"
                            subtitle="Simple and transparent bank payment pricing for affiliates using Montonio payment links."
                        />

                        <Grid container spacing={4} justifyContent="center">
                            <Grid item xs={12} md={8}>
                                <InfoCard
                                    icon={<CompareArrowsIcon />}
                                    title="General Pricing"
                                    description={`
                                        - Monthly fee: <strong>0€ / month</strong><br>
                                        - Payment link fee: <strong>10€ / month</strong><br>
                                        - Bank payment fee: <strong>0.10€ per transaction</strong><br>
                                        - Consumer card payment fee: <strong>0.99% + 0.12€ per transaction</strong><br>
                                        - Business card payment fee: <strong>1.99% + 0.12€ per transaction</strong><br>
                                        - Secure bank links<br>
                                        - Monthly invoice from Montonio<br>
                                        - Manage all payments in Montonio dashboard
                                    `}
                                    buttonText="Learn More"
                                    buttonLink="https://montonio.com/et/hinnapaketid/"
                                    delay={0.3}
                                />
                            </Grid>
                        </Grid>

                        <Paper
                            component={motion.div}
                            initial={{ y: 20, opacity: 0 }}
                            whileInView={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.4, duration: 0.5 }}
                            viewport={{once: true}}
                            elevation={1}
                            sx={{
                                mt: 4,
                                p: { xs: 2.5, md: 4 },
                                borderRadius: 4,
                                backgroundColor: colors.white,
                                border: `1px solid ${alpha(colors.primary, 0.12)}`
                            }}
                        >
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 700,
                                    mb: 1,
                                    fontFamily: '"Montserrat", sans-serif',
                                    color: colors.secondary
                                }}
                            >
                                Supported Montonio Bank Payments
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
                                Montonio supports bank payments across the following countries and banks.
                            </Typography>

                            <Grid container spacing={2.5}>
                                {montonioSupportedPayments.map((item) => (
                                    <Grid item xs={12} md={6} key={item.country}>
                                        <Box
                                            sx={{
                                                height: "100%",
                                                p: 2,
                                                borderRadius: 3,
                                                backgroundColor: alpha(colors.primary, 0.04),
                                                border: `1px solid ${alpha(colors.primary, 0.1)}`
                                            }}
                                        >
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.75 }}>
                                                {item.country}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                                {item.methods}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>

                        </Paper>

                        <Paper
                            component={motion.div}
                            initial={{ y: 20, opacity: 0 }}
                            whileInView={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.5, duration: 0.5 }}
                            viewport={{once: true}}
                            elevation={1}
                            sx={{
                                mt: 4,
                                p: { xs: 2.5, md: 4 },
                                borderRadius: 4,
                                backgroundColor: colors.white,
                                border: `1px solid ${alpha(colors.primary, 0.12)}`
                            }}
                        >
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 700,
                                    mb: 1,
                                    fontFamily: '"Montserrat", sans-serif',
                                    color: colors.secondary
                                }}
                            >
                                Supported Montonio Card Payments
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
                                Card payments include Visa and Mastercard. Apple Pay and Google Pay are also
                                available through card payments. Country-specific mobile payment methods are
                                listed separately below.
                            </Typography>

                            <Grid container spacing={2.5}>
                                <Grid item xs={12} md={6}>
                                    <Box
                                        sx={{
                                            height: "100%",
                                            p: 2,
                                            borderRadius: 3,
                                            backgroundColor: alpha(colors.primary, 0.04),
                                            border: `1px solid ${alpha(colors.primary, 0.1)}`
                                        }}
                                    >
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.75 }}>
                                            Finland 🇫🇮
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            MobilePay
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Box
                                        sx={{
                                            height: "100%",
                                            p: 2,
                                            borderRadius: 3,
                                            backgroundColor: alpha(colors.primary, 0.04),
                                            border: `1px solid ${alpha(colors.primary, 0.1)}`
                                        }}
                                    >
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.75 }}>
                                            Poland 🇵🇱
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            BLIK
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>

                        <Divider
                            component={motion.div}
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ delay: 0.6 }}
                            viewport={{once: true}}
                            sx={{
                                my: 6,
                                "&::before, &::after": {
                                    borderColor: alpha(colors.primary, 0.2),
                                }
                            }}
                        />

                        {/** IMAGE SECTION - SAVINGS **/}
                        <Grid container spacing={6} alignItems="center" sx={{ mb: 6 }}>
                            <Grid item xs={12} md={6} sx={{display: {xs: 'none', md: 'block'}}}>
                                <Box
                                    component={motion.div}
                                    initial={{x: -50, opacity: 0}}
                                    whileInView={{x: 0, opacity: 1}}
                                    transition={{duration: 0.6}}
                                    viewport={{once: true}}
                                    sx={{borderRadius: 4, overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.1)'}}
                                >
                                    <img src="/pic/blog-1.jpg" alt="Real Savings" style={{width: '100%', height: 'auto', display: 'block'}} />
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box
                                    component={motion.div}
                                    initial={{x: 50, opacity: 0}}
                                    whileInView={{x: 0, opacity: 1}}
                                    transition={{duration: 0.6}}
                                    viewport={{once: true}}
                                >
                                    <Typography
                                        variant="h3"
                                        sx={{
                                            fontWeight: 700,
                                            mb: 3,
                                            fontFamily: '"Montserrat", sans-serif',
                                            position: 'relative',
                                            display: 'inline-block'
                                        }}
                                    >
                                        Real Savings for Your Business
                                        <Box sx={{
                                            position: 'absolute',
                                            height: 4,
                                            width: 60,
                                            backgroundColor: colors.accent,
                                            bottom: -10,
                                            left: 0,
                                            borderRadius: 2
                                        }}/>
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary" sx={{lineHeight: 1.8, fontSize: '1.1rem', mb: 2}}>
                                        Many other solutions (e.g. Stripe) charge around 1.4% + €0.25 per transaction.
                                        With Montonio, you can save significantly on every payment.
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary" sx={{lineHeight: 1.8, fontSize: '1.1rem'}}>
                                        By choosing our application integrated with Montonio, you save significantly on transaction
                                        fees—allowing you to reinvest in your box and community.
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        {/** Võrdluse SEKTSIOON - 200 makset, keskmine 80€ **/}
                        <SectionTitle
                            title="Cost Comparison Example"
                            subtitle="Adjust monthly payments and see estimated Stripe vs Montonio bank payment cost."
                            align="left"
                            delay={0.4}
                        />

                        <Paper
                            component={motion.div}
                            initial={{ y: 20, opacity: 0 }}
                            whileInView={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.45, duration: 0.5 }}
                            viewport={{ once: true }}
                            elevation={1}
                            sx={{
                                p: { xs: 2.5, md: 4 },
                                mb: 4,
                                borderRadius: 3,
                                border: `1px solid ${alpha(colors.primary, 0.15)}`
                            }}
                        >
                            <Typography variant="overline" sx={{ color: "text.secondary", letterSpacing: 1 }}>
                                Monthly processing volume
                            </Typography>
                            <Typography
                                variant="h5"
                                sx={{ fontWeight: 700, color: colors.primary, mb: 2, fontFamily: '"Montserrat", sans-serif' }}
                            >
                                {monthlyPayments} payments / month
                            </Typography>
                            <Slider
                                value={monthlyPayments}
                                onChange={(_, value) => setMonthlyPayments(value)}
                                min={10}
                                max={1000}
                                step={10}
                                marks={[
                                    { value: 100, label: "100" },
                                    { value: 500, label: "500" },
                                    { value: 1000, label: "1000" }
                                ]}
                                sx={{ maxWidth: 700 }}
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                                Assumption: average payment amount {formatCurrency(averagePaymentAmount)}.
                            </Typography>
                        </Paper>

                        <Grid container spacing={4}>
                            <Grid item xs={12} md={6}>
                                <ComparisonCard
                                    title="Typical Processing (Stripe)"
                                    content={`
                                        <p style="margin-bottom: 16px;"><strong>${monthlyPayments} payments</strong> x average <strong>€80</strong> = <strong>${formatCurrency(totalVolume)}</strong> total</p>
                                        <p>1.4% of ${formatCurrency(totalVolume)} = <strong>${formatCurrency(totalVolume * stripePercentFee)}</strong> <br/>
                                        + (${monthlyPayments} x €0.25) = <strong>${formatCurrency(monthlyPayments * stripeFixedFee)}</strong> <br/>
                                        <strong>Total = ${formatCurrency(stripeCost)}</strong></p>
                                    `}
                                    delay={0.5}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <ComparisonCard
                                    title="With Montonio"
                                    content={`
                                        <p style="margin-bottom: 16px;"><strong>${monthlyPayments} payments</strong> x average <strong>€80</strong> = <strong>${formatCurrency(totalVolume)}</strong> total</p>
                                        <p style="margin-bottom: 16px;">(${monthlyPayments} x €0.10) = <strong>${formatCurrency(monthlyPayments * montonioFixedFee)}</strong><br/>
                                        + payment link fee <strong>${formatCurrency(montonioPaymentLinkMonthlyFee)}</strong><br/>
                                        + monthly fee <strong>${formatCurrency(montonioMonthlyFee)}</strong><br/>
                                        <strong>Total = ${formatCurrency(montonioCost)}</strong></p>
                                        <p><strong>You save ${formatCurrency(savings)} per month.</strong></p>
                                    `}
                                    isHighlighted={true}
                                    delay={0.6}
                                />
                            </Grid>
                        </Grid>

                        <Typography
                            variant="body2"
                            component={motion.p}
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ delay: 0.7 }}
                            viewport={{once: true}}
                            sx={{
                                mt: 4,
                                color: 'text.secondary',
                                textAlign: 'center'
                            }}
                        >
                            *Calculation uses Montonio bank payment pricing (0.10€ per payment, 10€ payment link monthly fee, 0€ monthly fee). Card payments may have different pricing.
                        </Typography>
                    </Container>
                </Box>

                {/** GETTING STARTED WITH MONTONIO - IMAGE SECTION **/}
                <Container sx={{ py: { xs: 6, md: 10 } }}>
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Box
                                component={motion.div}
                                initial={{x: -50, opacity: 0}}
                                whileInView={{x: 0, opacity: 1}}
                                transition={{duration: 0.6}}
                                viewport={{once: true}}
                            >
                                <Typography
                                    variant="h3"
                                    sx={{
                                        fontWeight: 700,
                                        mb: 3,
                                        fontFamily: '"Montserrat", sans-serif',
                                        position: 'relative',
                                        display: 'inline-block'
                                    }}
                                >
                                    Getting Started with Montonio
                                    <Box sx={{
                                        position: 'absolute',
                                        height: 4,
                                        width: 60,
                                        backgroundColor: colors.accent,
                                        bottom: -10,
                                        left: 0,
                                        borderRadius: 2
                                    }}/>
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{lineHeight: 1.8, fontSize: '1.1rem', mb: 3}}>
                                    Simply create an account on Montonio, then provide us with your API keys.
                                    Montonio will handle monthly invoices, and you can manage all transactions in their easy-to-use dashboard.
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    sx={{
                                        py: 1.5,
                                        px: 4,
                                        fontSize: "1rem",
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        textTransform: "none",
                                        boxShadow: "0 4px 14px rgba(0,114,229,0.3)",
                                        transition: "all 0.3s",
                                        "&:hover": {
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 8px 25px rgba(0,114,229,0.5)",
                                        }
                                    }}
                                    onClick={() => (window.location.href = "https://montonio.com/et/hinnapaketid/")}
                                >
                                    Create Montonio Account
                                </Button>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box
                                component={motion.div}
                                initial={{x: 50, opacity: 0}}
                                whileInView={{x: 0, opacity: 1}}
                                transition={{duration: 0.6}}
                                viewport={{once: true}}
                                sx={{borderRadius: 4, overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.1)'}}
                            >
                                <img src="/pic/blog-2.jpg" alt="Get Started" style={{width: '100%', height: 'auto', display: 'block'}} />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>

                {/* CTA SECTION */}
                <Box sx={{backgroundColor: alpha(colors.primary, 0.03), py: {xs: 6, md: 10}}}>
                    <Container maxWidth="md">
                        <Box
                            component={motion.div}
                            initial={{scale: 0.95, opacity: 0}}
                            whileInView={{scale: 1, opacity: 1}}
                            viewport={{once: true}}
                            transition={{duration: 0.5}}
                            sx={{
                                textAlign: "center",
                                p: {xs: 3, md: 6},
                                borderRadius: 4,
                                backgroundColor: 'white',
                                boxShadow: '0 10px 40px rgba(0,0,0,0.05)',
                            }}
                        >
                            <Typography
                                variant={isMobile ? "h4" : "h3"}
                                sx={{fontWeight: 700, mb: 2, fontFamily: '"Montserrat", sans-serif'}}
                            >
                                Ready to Transform Your Box?
                            </Typography>
                            <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{mb: 4, maxWidth: 600, mx: 'auto', fontSize: {xs: "1rem", md: "1.1rem"}, lineHeight: 1.6}}
                            >
                                Join hundreds of CrossFit affiliate owners who are saving money and streamlining their operations.
                                Start your free 3-month trial today—no credit card required.
                            </Typography>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={() => navigate("/register")}
                                sx={{
                                    py: 1.5,
                                    px: 5,
                                    fontSize: "1.1rem",
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    textTransform: "none",
                                    boxShadow: "0 4px 14px rgba(0,114,229,0.3)",
                                    transition: "all 0.3s",
                                    "&:hover": {
                                        transform: "translateY(-3px)",
                                        boxShadow: "0 8px 25px rgba(0,114,229,0.5)",
                                    }
                                }}
                            >
                                Get Started Today
                            </Button>
                        </Box>
                    </Container>
                </Box>

                {/** FOOTER **/}
                <Box sx={{backgroundColor: colors.secondary, color: colors.white, py: 4}}>
                    <Container>
                        <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5}}>
                            <Typography variant="body2" align="center">
                                © {new Date().getFullYear()} Irontrack OÜ | All rights reserved.
                            </Typography>
                            <Typography variant="body2" align="center" sx={{opacity: 0.8}}>
                                Reg. nr: 17184398 | Email: info@irontrack.ee
                            </Typography>
                            <Box sx={{mt: 1}}>
                                <a
                                    href="/privacy-policy"
                                    style={{color: 'white', textDecoration: 'none', fontSize: '0.875rem'}}
                                >
                                    Privacy Policy
                                </a>
                            </Box>
                        </Box>
                    </Container>
                </Box>
            </Box>
        </>
    );
}